/**
 * VatSummaryPage
 * 부가세 분기별 정리 페이지
 */

import React, { useState, useEffect, useCallback } from 'react';
import { TaxRepository } from '../../repositories/TaxRepository';
import { formatKRW } from '../../models/Tax';

const taxRepository = new TaxRepository();

interface VatMonth {
  month: number;
  output_tax: number;  // 매출세액
  input_tax: number;   // 매입세액
  payable: number;     // 납부세액
}

interface VatData {
  year: number;
  quarter: number;
  months: VatMonth[];
  total: {
    output_tax: number;
    input_tax: number;
    payable: number;
  };
}

const QUARTER_LABELS: Record<number, string> = {
  1: '1기 예정 (1~3월)',
  2: '1기 확정 (4~6월)',
  3: '2기 예정 (7~9월)',
  4: '2기 확정 (10~12월)',
};

const MONTH_NAMES = ['', '1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

export const VatSummaryPage: React.FC = () => {
  const now = new Date();
  const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(currentQuarter);
  const [data, setData] = useState<VatData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await taxRepository.getVatSummary(year, quarter);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : '부가세 정리 로드 실패');
    } finally {
      setLoading(false);
    }
  }, [year, quarter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const yearOptions = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i);

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">부가세 정리</h1>
        <div className="flex gap-2">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}년</option>)}
          </select>
          <select
            value={quarter}
            onChange={(e) => setQuarter(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            {[1, 2, 3, 4].map((q) => (
              <option key={q} value={q}>{QUARTER_LABELS[q]}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">불러오는 중...</div>
      ) : data ? (
        <>
          {/* 분기 요약 카드 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500 mb-1">매출세액 (납부할 세금)</p>
              <p className="text-3xl font-bold text-green-600">{formatKRW(data.total.output_tax)}</p>
              <p className="text-xs text-gray-400 mt-2">매출 부가세 + 매출 세금계산서 세액</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500 mb-1">매입세액 (공제받을 세금)</p>
              <p className="text-3xl font-bold text-orange-600">{formatKRW(data.total.input_tax)}</p>
              <p className="text-xs text-gray-400 mt-2">경비 부가세 + 매입 세금계산서 세액</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6 border-2 border-blue-200">
              <p className="text-sm text-gray-500 mb-1">
                {data.total.payable >= 0 ? '납부할 부가세' : '환급받을 부가세'}
              </p>
              <p className={`text-3xl font-bold ${data.total.payable >= 0 ? 'text-red-600' : 'text-blue-600'}`}>
                {data.total.payable >= 0 ? formatKRW(data.total.payable) : formatKRW(Math.abs(data.total.payable))}
              </p>
              <p className="text-xs text-gray-400 mt-2">매출세액 - 매입세액</p>
            </div>
          </div>

          {/* 안내 */}
          <div className="bg-blue-50 rounded-md p-4">
            <p className="text-sm text-blue-800 font-medium">부가세 신고 안내</p>
            <ul className="text-xs text-blue-700 mt-1 space-y-0.5 list-disc list-inside">
              <li>개인사업자: 1기(1~6월) 7월 25일까지, 2기(7~12월) 다음해 1월 25일까지 신고</li>
              <li>예정신고: 1기 예정(1~3월) 4월 25일, 2기 예정(7~9월) 10월 25일</li>
              <li>이 페이지의 금액은 참고용입니다. 정확한 신고는 세무사와 상의하세요.</li>
            </ul>
          </div>

          {/* 월별 상세 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">월</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">매출세액</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">매입세액</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">납부/환급</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.months.map((m) => (
                    <tr key={m.month} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{MONTH_NAMES[m.month]}</td>
                      <td className="px-6 py-4 text-sm text-green-600 text-right">{m.output_tax.toLocaleString()}원</td>
                      <td className="px-6 py-4 text-sm text-orange-600 text-right">{m.input_tax.toLocaleString()}원</td>
                      <td className={`px-6 py-4 text-sm font-medium text-right ${m.payable >= 0 ? 'text-red-600' : 'text-blue-600'}`}>
                        {m.payable >= 0 ? `${m.payable.toLocaleString()}원` : `-${Math.abs(m.payable).toLocaleString()}원`}
                      </td>
                    </tr>
                  ))}
                  {/* 합계 행 */}
                  <tr className="bg-gray-100 font-bold">
                    <td className="px-6 py-4 text-sm text-gray-900">분기 합계</td>
                    <td className="px-6 py-4 text-sm text-green-700 text-right">{data.total.output_tax.toLocaleString()}원</td>
                    <td className="px-6 py-4 text-sm text-orange-700 text-right">{data.total.input_tax.toLocaleString()}원</td>
                    <td className={`px-6 py-4 text-sm text-right ${data.total.payable >= 0 ? 'text-red-700' : 'text-blue-700'}`}>
                      {data.total.payable >= 0 ? `${data.total.payable.toLocaleString()}원` : `-${Math.abs(data.total.payable).toLocaleString()}원`}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};
